/**
 * Price-expression evaluation and rounding policy.
 *
 * Mirrors QPC `get_tariff_price_form_items` (src/calculator/calculator.c:16):
 * an ordered list of components, each `(qty - skip) * value` clamped to
 * [min_value, max_value], accumulated onto a flat base.
 *
 * Two QPC behaviours deliberately NOT ported:
 *   - the `multiply` component id (unused in every production config)
 *   - the `pri < 0 ? 1/-pri : pri` sentinel (an unused escape hatch)
 */

import type { Money, PriceExpr, Quantities, RoundingMode } from './types';

/**
 * Evaluate a price expression over one placement.
 *
 * @param nights  nights covered by this placement
 * @param from    night index the placement starts at (for per-night quantities)
 *
 * Note: a placement's price does NOT scale with its length unless `perNight`
 * is set. QPC charges the full piece price even when a placement is trimmed
 * short (basic_tariff_get_entry always returns `meta->price`), and fixture
 * `14-static-arrangement-short-after` depends on exactly that.
 */
export function evaluatePrice(
  expr: PriceExpr | undefined,
  qty: Quantities,
  nights: number,
  from: number,
): Money {
  if (!expr) return 0;
  let total = expr.base ?? 0;
  if (expr.perNight) total += expr.perNight * nights;

  if (expr.per) {
    for (const c of expr.per) {
      const vec = qty[c.quantity];
      if (!vec) continue; // absent quantity contributes nothing (QPC: id >= items.length)

      let v: number;
      if (c.perNight) {
        let acc = 0;
        for (let d = from; d < from + nights; d++) {
          acc += Math.max(0, (vec[d] ?? 0) - (c.freeUnits ?? 0)) * c.amount;
        }
        v = acc;
      } else {
        // Non-per-night components read the placement's peak occupancy, so a
        // party that changes mid-stay is charged on the level it actually used.
        let peak = 0;
        for (let d = from; d < from + nights; d++) peak = Math.max(peak, vec[d] ?? 0);
        v = Math.max(0, peak - (c.freeUnits ?? 0)) * c.amount;
      }

      if (c.min != null && v < c.min) v = c.min;
      if (c.max != null && c.max > 0 && v > c.max) v = c.max;
      total += v;
    }
  }
  return total;
}

/**
 * Rounding policy, applied once to the final total.
 *
 * ---------------------------------------------------------------------------
 * THE CENT IS A FLOOR, NOT A CHOICE
 * ---------------------------------------------------------------------------
 *
 * There is no such thing as a price of €1129.6333: money does not go below the
 * cent, so neither does a quote. `none` therefore means "to the cent" and not
 * "to whatever a percentage left behind" — it was the latter, and a quarter of
 * this park's prices would have carried a third decimal into a booking form the
 * moment its rounding was turned off.
 *
 * `cents` remains as a synonym rather than being deleted: cards already say it,
 * and it now describes what every mode does anyway.
 *
 * `psychological` reproduces QPC's last-digit table (calculatorWorker.js
 * normalize mode 3): pull the euro amount toward a multiple of 5 — but only
 * from 1, 2, 3 and 4, so 6 through 9 are left where they are. LEGACY, and kept
 * only for the cards that already carry it; see the note on `RoundingMode`.
 * QPC's mode 3 then falls through to mode 4 for want of a `break`; that is a
 * no-op on an already-integer value, so it is not reproduced.
 */
export function applyRounding(value: Money, mode: RoundingMode | undefined): Money {
  switch (mode) {
    case 'euro':
      return Math.round(value);
    case 'tenCents':
      return Math.round(value * 10) / 10;
    case 'psychological': {
      let price = Math.round(value);
      switch (Math.abs(price % 10)) {
        case 1: price -= 1; break;
        case 2: price -= 2; break;
        case 3: price += 2; break;
        case 4: price += 1; break;
        default: break;
      }
      return price;
    }
    case 'cents':
    case 'none':
    default:
      // The floor. See the header.
      return Math.round(value * 100) / 100;
  }
}

/** Higher mode wins when several modifiers each request rounding (QPC semantics). */
const ROUNDING_RANK: Record<RoundingMode, number> = {
  none: 0,
  cents: 1,
  euro: 2,
  psychological: 3,
  tenCents: 4,
};

export function strongestRounding(
  a: RoundingMode | undefined,
  b: RoundingMode | undefined,
): RoundingMode | undefined {
  if (!a) return b;
  if (!b) return a;
  return ROUNDING_RANK[a] >= ROUNDING_RANK[b] ? a : b;
}

/** Broadcast scalars / arrays into per-night Int32Array vectors. */
export function normaliseQuantities(
  input: Record<string, number | number[] | Int32Array>,
  nights: number,
): Quantities {
  const out: Quantities = {};
  for (const [key, value] of Object.entries(input)) {
    const vec = new Int32Array(nights);
    if (typeof value === 'number') {
      vec.fill(value);
    } else {
      for (let d = 0; d < nights; d++) vec[d] = Number(value[d] ?? 0);
    }
    out[key] = vec;
  }
  return out;
}
